"use client";

// WO-COMMERCE-BIND-001 (ENG-SOT-048) · DoD ROW 7 — URL SYNC, THE HOST'S HALF.
//
// Row 7: "In-app navigation drives the parent address bar to the real contract URL (pushState),
// back/forward drives the app (popstate), refresh at any point reloads the same screen."
//
// THE RULE THAT SHAPES THIS FILE: never push a URL the state does not uniquely name. The export's
// coarse state left 259 of 402 slugs sharing an address — one state was the state of THIRTY-ONE
// different operator pages — and pushing from that would put another operator's URL in the bar while
// you look at this one. Adding the export's `dEnt` to the map cuts that to 114, and of those 114,
// 108 are a listing page and its own product-type children, where the listing IS the honest target
// (the export renders the same screen for all of them). The remaining handful get NO push and the
// address bar holds its last known-good value — an address that is merely stale beats one that is
// confidently wrong, because a wrong URL is shareable, indexable and ends up in support tickets.
//
// SECURITY: the export is same-origin (the host serves the file), so both directions pin
// `location.origin`. A `"*"` targetOrigin would hand the user's navigation to any page that framed
// us, and an unchecked `event.origin` would let any framed page drive this one's address bar.
//
// REFRESH is already honest and needs no code: the URL pushed here is a real contract path that the
// server renders with the matching `?dc=` state patch — the same map, read from the other end.

import { useEffect, useRef } from "react";
import { mountainOfPath as mountainCodeOfPath, urlSyncDecision } from "@/lib/url-sync-policy";
import URL_MAP from "@/data/export-url-map.json";

type Row = [slug: string, path: string, page: string, mountain: string, dCat: string, dEnt: string, lMode: string, rSlug: string];
type ExportState = { page?: string; mountain?: string; dCat?: string; dEnt?: string; lMode?: string; rSlug?: string };

// Assert the ARRAY, not the wrapping object. The generated file carries diagnostic fields
// (_source, _note, _slugs, …) beside `entries`, so `URL_MAP as { entries: Row[] }` overlaps in
// neither direction and TS2352s. `Row[]` IS assignable to the inferred `string[][]`, so asserting
// the inner array is legal on its own — and unlike `as unknown as`, it still fails loudly if the
// generator ever stops emitting rows of strings. (Ported from pr35-check in FWLR-025.)
const ROWS = URL_MAP.entries as Row[];

// `lMode` is deliberately NOT part of the key. The state patch table sets it on ZERO of the 402 rows,
// so it carries no discriminating power — while the export broadcasts its live value ("transport") on
// every message. Keying on it therefore matched nothing at all: every push silently became a hold, and
// the sync looked like it was never wired. It stays in the row (and in the broadcast) as a record of
// what the export knows; it is simply not evidence about WHICH url. `page` already separates the
// transport / market / directory faces of a mountain.
const keyOf = (s: ExportState) =>
  [s.page ?? "", s.mountain ?? "", s.dCat ?? "", s.dEnt ?? "", s.rSlug ?? ""].join("|");

/** state key -> every slug that renders it. Built once. */
const BY_STATE = (() => {
  const m = new Map<string, Row[]>();
  for (const r of ROWS) {
    const k = [r[2], r[3], r[4], r[5], r[7]].join("|");
    const list = m.get(k);
    if (list) list.push(r);
    else m.set(k, [r]);
  }
  return m;
})();

const BY_PATH = new Map(ROWS.map((r) => [r[1], r]));

// ── WO-COMMERCE-BIND-001 §29 — CROSS-MOUNTAIN AND HOME MOVES ──────────────────────────────────────
//
// THE DEFECT, reproduced verbatim before a line of this was written: land `/mountain-whistler`,
// switch mountain in the header to Mont Tremblant. The screen becomes Mont Tremblant ("Reserve Mont
// Tremblant buses, shuttles & airport transfers"), the export broadcasts the move correctly —
//   {"page":"mtn-home","mountain":"Mont Tremblant","dCat":"mountain","dEnt":"","lMode":"transport"}
// — and the address bar, the <title> and the canonical all stay Whistler's.
//
// ROOT CAUSE, and it is a MISS, not a missing wire. `keyOf` builds `mtn-home|Mont Tremblant|mountain||`
// from that broadcast. The map's row for `/mountain-tremblant` is
// `['mountain-tremblant','/mountain-tremblant','mtn-home','Mont Tremblant','','','','']` — dCat EMPTY.
// No row anywhere carries (mtn-home, *, mountain): all 30 dCat="mountain" rows are `/…/profile`
// directory pages. So `BY_STATE` misses, `resolvePath` returns null, and this file's documented
// behaviour on a miss is to HOLD the bar. Working exactly as written, over a state the map never
// described — the map's columns come from the deep-link PATCH (which sets only what a page needs)
// while the export's LIVE state carries more (`dCat:"mountain"` here, `page:"home"` for in-app home,
// against a home row whose page column is empty). Same class, both halves.
//
// WHY THIS IS A REAL NAVIGATION AND NOT A REPAIRED pushState. `history.pushState` cannot change the
// document's <title> or its canonical link, and the founder's report names the title. A pushState fix
// would move the address bar and leave the tab, the canonical and every crawler still reading
// Whistler on a Mont Tremblant screen — a URL that is confidently wrong in a new way. Only a real
// route change makes the server re-render the right surface with the right head.
//
// ⚠️ TWO CORRECTIONS TO §29's OWN TEXT, measured on this build and recorded rather than inherited:
//   1. §29 says "`/mountain-mont-tremblant` serves NO export iframe". That URL is a **404** — the slug
//      does not exist. The contract URL is `/mountain-tremblant`, and it answers **200 WITH an export
//      iframe**, its own title ("Daily Buses & Shuttles - Mont Tremblant YUL | SNOWBUS") and its own
//      canonical. The "no iframe" reading was taken against a URL that was never served.
//   2. Which makes the naming rule load-bearing: a display name is NOT a slug. "Mont Tremblant"
//      slugifies to `mont-tremblant`, and that is the 404. The target is therefore looked up in the
//      MAP, exact-match on the name the export itself broadcasts — the §28.1 authored-table rule
//      again. An unknown name HOLDS the bar; it never gets slugified into a guess.
//
/** Every mountain's own landing page, by the display name the export broadcasts. 30/30, exact. */
const MTN_HOME_BY_NAME = new Map(
  ROWS.filter((r) => r[2] === "mtn-home" && !r[4] && !r[5] && !r[7] && r[3]).map((r) => [r[3], r[1]]),
);

/** Longest matching mountain-home path that owns this URL — so `/mountain-whistler/booking/...`
 *  answers "Whistler" rather than "no mountain". Prefix-matched against AUTHORED paths only.
 *  Getting this wrong is how a cross-mountain rule would fire mid-funnel and throw a cart away. */
function mountainOfPath(path: string): string {
  let best = "";
  let bestLen = -1;
  for (const [name, p] of MTN_HOME_BY_NAME) {
    if ((path === p || path.startsWith(p + "/")) && p.length > bestLen) {
      best = name;
      bestLen = p.length;
    }
  }
  return best;
}

/**
 * The one URL this state names, or null to hold the address bar where it is.
 *
 * With several candidates, the only case that resolves is a page and its own children: if exactly one
 * candidate slug is a strict prefix of every other, that one is the parent listing and it is what the
 * export is rendering. Anything else — genuinely distinct pages sharing a state — refuses.
 */
export function resolvePath(state: ExportState): string | null {
  const hits = BY_STATE.get(keyOf(state));
  if (!hits || hits.length === 0) return null;
  if (hits.length === 1) return hits[0]![1];
  const parents = hits.filter((c) => hits.every((o) => o === c || o[0].startsWith(c[0] + "_")));
  return parents.length === 1 ? parents[0]![1] : null;
}

// ── WO-URL-NAV-001 · NAV-2 — RESOLVE BY THE MAP'S OWN SEMANTICS, NOT BY TUPLE EQUALITY ────────────
//
// MEASURED (broadcast-trace.mjs, home page, deployed build): clicking "View all" / "My trips" /
// "Marketplace" broadcasts a full live state —
//   {"page":"market","mountain":"Whistler","dCat":"mountain","dEnt":"","lMode":"transport","rSlug":""}
// — and the address bar holds at "/". The design side WORKS; the miss is here. `keyOf` compares
// dCat verbatim, the live state carries dCat:"mountain", and the market/trips/faq rows record
// dCat:"" — so the tuple can never match. This is the SAME class §29 already fixed for `lMode`
// ("the map's columns come from the deep-link PATCH while the export's LIVE state carries more"),
// now closed for every column at once instead of one exception per burn.
//
// The map's own header declares the semantics: "Empty string = not set." Not-set is a WILDCARD,
// not a literal. So: a row matches a state when every column the row DOES set equals the state's
// value. Among matches — most specific wins (most set-and-matched columns: the Whistler market row
// beats the global market row when the state names Whistler, which is also what legacy does: its
// footer marketplace link from a mountain page is mountain-scoped). Ties fall to §29's own
// parent-listing rule, and anything still ambiguous HOLDS — this file's standing doctrine.
//
// THE ECHO GUARD, and why it is load-bearing: deep-link /deals-and-packages, and the mounted
// design broadcasts its state back with its default mountain filled in ("Whistler"). Without the
// guard that echo would resolve to /mountain-whistler/deals-and-packages and REDIRECT a direct
// load of a canonical global page — the crawler's own URL — to a different address. So: if the
// path we are ALREADY AT is among the matching rows, the address is a truthful name for this
// state, and a truthful address is never traded for a more specific one. Hold.
export function resolveWildcard(state: ExportState, here: string): string | null {
  const s = [state.page ?? "", state.mountain ?? "", state.dCat ?? "", state.dEnt ?? "", state.lMode ?? "", state.rSlug ?? ""];
  let best: Row[] = [];
  let bestScore = -1;
  let hereMatches = false;
  for (const r of ROWS) {
    const cols = [r[2], r[3], r[4], r[5], r[6], r[7]];
    let score = 0;
    let ok = true;
    for (let i = 0; i < 6; i++) {
      const set = cols[i] !== "";
      if (set && cols[i] !== s[i]) { ok = false; break; }
      if (set) score++;
    }
    if (!ok) continue;
    // The echo guard holds only when the current path AGREES ON THE PAGE CLASS. Measured on the
    // deployed build (broadcast-trace.mjs): the home row sets ZERO columns, so "/" wildcard-matches
    // every state — and an unconditional hold ate every navigation off the homepage ("My trips"
    // broadcast fired, address stayed "/"). A row whose page column is unset cannot vouch that the
    // current address names the state's page; only a set-and-equal page column can.
    if (r[1] === here && (r[2] !== "" ? r[2] === (state.page ?? "") : (state.page ?? "") === "")) hereMatches = true;
    if (score > bestScore) { best = [r]; bestScore = score; }
    else if (score === bestScore) best.push(r);
  }
  if (hereMatches) return here;                            // the echo guard — already truthful
  if (best.length === 0) return null;
  if (best.length === 1) return best[0]![1];
  const parents = best.filter((c) => best.every((o) => o === c || o[0].startsWith(c[0] + "_")));
  return parents.length === 1 ? parents[0]![1] : null;     // still ambiguous — hold
}

/** The state a contract path deep-links to — the same map read backwards, for popstate. */
export function stateForPath(path: string): ExportState | null {
  const r = BY_PATH.get(path);
  return r ? { page: r[2], mountain: r[3], dCat: r[4], dEnt: r[5], lMode: r[6], rSlug: r[7] } : null;
}

/**
 * WHO OWNS THE ENTRY A BACK/FORWARD TRAVERSAL LANDED ON, and so what — if anything — this listener does. popstate only fires for an
 * entry of THIS document (a traversal to another document is a browser document load, not an event here). A mapped address is NOT, by
 * itself, a state the design can show: the design in this document holds the data, head and supplier/store payload the SERVER delivered for
 * `servedPath`, and a design message changes none of those. So a design message is sent only when the surface positively owns the target:
 *   · the entry is the one this component itself pushed (`sgUrlSync` — Next copies `__NA` into those too, so `__NA` alone proves nothing)
 *     AND its mountain is the mountain the server delivered; or
 *   · the target IS the address the server delivered for this document (the document's own entry).
 * Everything else is somebody else's:
 *   · "router"  — an entry the Next AppRouter created (`__NA`): it restores its tree and fetches that route's data and head itself;
 *   · "reload"  — an entry nobody here owns: the server owns the address, so load it once.
 * With no served context (`servedPath` absent) nothing is claimed for the design. It never consults an iframe or messages another window.
 */
export type PopAction = { action: "none" } | { action: "navigate"; state: ExportState } | { action: "router" } | { action: "reload" };
const normPath = (p: string) => p.replace(/\/+$/, "") || "/";
export function popAction({ here, selfPushed, historyState, servedPath }: { here: string; selfPushed: string | null; historyState: unknown; servedPath?: string }): PopAction {
  if (selfPushed === here) return { action: "none" };
  const hs = historyState && typeof historyState === "object" ? (historyState as { __NA?: unknown; sgUrlSync?: unknown }) : null;
  const routerOwned = hs?.__NA === true;
  const foreign: PopAction = routerOwned ? { action: "router" } : { action: "reload" };
  const st = stateForPath(here);
  if (!st || servedPath === undefined) return foreign;
  const served = normPath(servedPath);
  const documentsOwnEntry = here === served;
  // the mountain comes from the SERVER-delivered path and the target path, compared with each other — never from the title or the pop alone
  const ownPushSameData = hs?.sgUrlSync === true && mountainCodeOfPath(served) === mountainCodeOfPath(here);
  return documentsOwnEntry || ownPushSameData ? { action: "navigate", state: st } : foreign;
}

// ── NEXT-MAPPED · A ROUTER PUSH ACROSS MOUNTAINS MUST STAY ONE SOFT ENTRY (L-RELEASE 2026-10-25) ───────────────────────────────────────
//
// MEASURED (url-sync gate NEXT-MAPPED leg, local read-only history trace): land /mountain-whistler, `next.router.push("/mountain-tremblant")`.
// History length went 2 → 4, not 3. At t+0 `beforeunload` fired on Whistler's document (a `location.assign`), and 23ms later Next's own
// `pushState` for Tremblant landed. One push, two entries: Next's soft entry and a hard document load. Back then stepped from the hard
// Tremblant document onto the soft Tremblant entry — the same screen — so Back "did nothing". The failure was made at the PUSH; Back only
// exposed it.
//
// WHY, from the code: the router renders the destination route in a React transition and commits it afterwards (and only then does its
// HistoryUpdater push the entry). Rendering the destination's design runs `renderVals()`, whose first act is `_sgBroadcast()` — the opening
// screen of a design that is NOT committed yet. That message is a task on `window`, so it is delivered while React yields mid-render, to the
// only sync that is mounted: the DEPARTING route's. That sync read "Mont Tremblant" from a design it does not host, against an address still
// naming Whistler, and the cross-mountain rule answered with a real navigation to the very page the router was already delivering.
//
// THE CLOSURE IS OWNERSHIP, NOT TIMING. A broadcast now carries `served` — the route path the server rendered the broadcasting design for
// (design-sources `DC_BROADCAST_SERVED`, the same `path` this component receives as `servedPath`, both from the same server expression). A sync
// only acts for the design of its own route; a message from another route's design is not about this surface, so it is not this surface's to
// answer. A legitimate in-document move (a map pin, a control without an href) comes from the design this sync hosts, so `served` matches and
// the cross-mountain navigation is unchanged. A message without `served` (a test or a probe posting the bare shape) is judged as before.
export function broadcastIsOurs(served: unknown, servedPath: string | undefined): boolean {
  if (typeof served !== "string" || served === "" || servedPath === undefined) return true;
  return normPath(served) === normPath(servedPath);
}

export default function ExportUrlSync({ servedPath }: { /** the route path the server rendered this document/route for (real server props, not read from the browser) */ servedPath?: string } = {}) {
  // The URL this component itself pushed. popstate has no way to say who caused it, so without this
  // the pushed URL echoes back into the export as a "navigation" and the two halves ping-pong.
  const selfPushed = useRef<string | null>(null);
  // §29 — ONE host navigation per page load, and no more. If a mountain's own `?dc=` patch ever
  // disagreed with the mountain whose URL it was served at, the two would bounce a customer between
  // two addresses forever with no way out. Budgeting it means that failure degrades to this file's
  // existing doctrine — a stale address beats a confidently wrong one — and says so in the console
  // instead of trapping anyone. Measured on this build: the patch and the URL agree on both mountains
  // tested, so this belt is never reached today; it exists because the cost of being wrong is a loop.
  const navBudget = useRef(1);
  // The server-delivered route for the CURRENT render. A Next route restore re-renders this component with the restored route's props, so
  // the listener (installed once) reads the latest value through the ref rather than a closure from the first render.
  const servedRef = useRef<string | undefined>(servedPath);
  useEffect(() => {
    servedRef.current = servedPath;
  }, [servedPath]);

  useEffect(() => {
    // ── QA-BATCH-2 · item 10 (2026-09-17) — THE ADDRESS FOLLOWS THE SCREEN; ANCHORS STILL OWN NAVIGATION ──
    //
    // WO-URL-NAV-002 (founder 2026-08-18 §7) took every state-driven NAVIGATION out of this listener
    // because it raced real anchors (the note below is kept verbatim: it is the measurement). The
    // founder's 2026-09-17 batch then found the cost of holding on every state: a screen reached
    // through a control WITHOUT a minted href — Research → a vendor or category, Research → Transport
    // from a control the contract had no row for — changed the page and left the previous route in the
    // address bar. Both findings are honoured by `urlSyncDecision` (lib/url-sync-policy.ts, pure and
    // unit-tested): within a mountain the address is PUSHED (no document load, nothing to race); a
    // recent real anchor activation HOLDS the policy so the browser's own navigation owns the address;
    // host-owned routes (the funnel, /cart) hold; only a cross-mountain state — where the served facts
    // and head would otherwise describe the wrong mountain — is a real navigation, and only once.
    let lastAnchor: { path: string; at: number } | null = null;
    let leaving = false;
    const onClickCapture = (ev: MouseEvent) => {
      if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      const a = (ev.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || (a.target && a.target !== "_self")) return;
      let url: URL;
      try { url = new URL(a.href, window.location.href); } catch { return; }
      if (url.origin !== window.location.origin) return;
      lastAnchor = { path: url.pathname.replace(/\/+$/, "") || "/", at: Date.now() };
    };
    const onLeave = () => { leaving = true; };

    /** A real route change: the server re-renders the surface, the title and the canonical. */
    const hostNavigate = (to: string, why: string) => {
      if (navBudget.current <= 0) {
        console.warn(`[sg:urlsync] holding the address bar: ${why} → ${to}, but this page has already navigated once.`);
        return;
      }
      navBudget.current -= 1;
      // deliberately WITHOUT window.location.search: the old query belongs to the mountain being
      // left. Carrying Whistler's search onto Mont Tremblant's page is the fabrication class again.
      window.location.assign(to);
    };

    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as { type?: string; state?: ExportState; served?: unknown } | null;
      if (!d || d.type !== "sg:urlstate" || !d.state) return;
      // A design that is not this route's (the destination of a router push, still being rendered) is not about this surface.
      if (!broadcastIsOurs(d.served, servedRef.current)) return;

      const here = window.location.pathname.replace(/\/+$/, "") || "/";

      // ── WO-URL-NAV-002 — ALL REMAINING ROUTE OWNERSHIP REMOVED (founder ruling 2026-08-18 §7) ───
      //
      // The in-app-home and cross-mountain branches used to navigate from here. They are gone, and
      // the reason is not tidiness — they were still WINNING RACES against real anchors. MEASURED
      // on the deployed f859ab0: the visible "Transport" control is a span inside
      // <a href="/mountain-whistler/bus-schedule">, exactly right, and the click still landed on
      // /mountain-whistler. The anchor started an ordinary navigation to the schedule; the export
      // then broadcast its state; this listener read "/" as owning no mountain, called that a
      // mountain switch, and hostNavigate'd to the hub — overtaking the anchor. State beat the URL
      // by a few milliseconds, which is the whole defect class in one frame.
      //
      // QA-BATCH-2 · item 10 re-admits ONE navigation (cross-mountain, budgeted) and otherwise pushes
      // the address, both behind the anchor window above — the race that ruling measured cannot recur
      // because a click on a real anchor holds this listener for ANCHOR_WINDOW_MS.
      // resolveWildcard carries the echo guard ("the path we are already at is a truthful name → hold")
      const resolved = resolveWildcard(d.state, here);
      const decision = urlSyncDecision({
        resolved,
        here,
        hereIsContract: stateForPath(here) !== null,
        resolvedMountain: resolved ? mountainCodeOfPath(resolved) : null,
        hereMountain: mountainCodeOfPath(here),
        lastAnchor,
        now: Date.now(),
        navigationsUsed: 1 - navBudget.current,
        leaving,
      });
      if (decision.action === "push") {
        selfPushed.current = decision.to;
        try { window.history.pushState({ sgUrlSync: true }, "", decision.to); } catch { /* a browser that refuses leaves the address as it was */ }
        return;
      }
      if (decision.action === "navigate") hostNavigate(decision.to, decision.why);
    };

    const onPop = () => {
      const here = window.location.pathname.replace(/\/+$/, "") || "/";
      const act = popAction({ here, selfPushed: selfPushed.current, historyState: window.history.state, servedPath: servedRef.current });
      if (act.action === "none") { selfPushed.current = null; return; }
      // A mapped address is a design state: the design renders in THIS document, so the existing sg:navigate receiver is addressed in this
      // window (same origin only). Back and Forward are the same event, so both restore without a second document load or a new entry.
      if (act.action === "navigate") { window.postMessage({ type: "sg:navigate", state: act.state }, window.location.origin); return; }
      // An unmapped entry the Next AppRouter owns is restored by the router on this same event; reloading here would be a second load.
      if (act.action === "router") return;
      // Anything else (a route page, the funnel, an entry nobody here owns): the server owns the address, so let it render it — a screen from
      // before the route change cannot survive, and the head and data come with the document.
      window.location.reload();
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("popstate", onPop);
    document.addEventListener("click", onClickCapture, true);
    window.addEventListener("pagehide", onLeave);
    window.addEventListener("beforeunload", onLeave);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("pagehide", onLeave);
      window.removeEventListener("beforeunload", onLeave);
    };
  }, []);

  return null;
}
